<script>
import { useAlert } from 'dashboard/composables';
import Branding from 'shared/components/Branding.vue';
import Spinner from 'shared/components/Spinner.vue';
import Rating from 'survey/components/Rating.vue';
import Feedback from 'survey/components/Feedback.vue';
import { getSurveyDetails, updateSurvey } from 'survey/api/survey';


export default {
  name: 'Response',
  components: {
    Branding,
    Rating,
    Spinner,
    Feedback,
  },
  data() {
    return {
      surveyDetails: null,
      isLoading: false,
      errorMessage: null,
      selectedRating: null,
      feedbackMessage: '',
      easeOfInteraction: null,
      issueResolution: true,
      isUpdating: false,
      isSubmitted: false,
      logo: '/brand-assets/logo.png',
      inboxName: '',
      inboxChannelName: '',
      surveyTitleImage: '/brand-assets/survey_title_image.png',
    };
  },
  computed: {
    surveyId() {
      const pageURL = window.location.href;
      return pageURL.substring(pageURL.lastIndexOf('/') + 1);
    },
    isRatingSubmitted() {
      return this.surveyDetails && this.surveyDetails.rating;
    },
    isFeedbackSubmitted() {
      return this.surveyDetails && this.surveyDetails.feedback_message;
    },
    isButtonDisabled() {
      return !this.selectedRating;
    },
    shouldShowBanner() {
      return this.isRatingSubmitted || this.errorMessage;
    },
    enableFeedbackForm() {
      return !this.isFeedbackSubmitted && this.isRatingSubmitted;
    },
    shouldShowErrorMesage() {
      return !!this.errorMessage;
    },
    shouldShowSuccessMesage() {
      return !!this.isRatingSubmitted;
    },
    message() {
      if (this.errorMessage) {
        return this.errorMessage;
      }
      return this.$t('SURVEY.RATING.SUCCESS_MESSAGE');
    },
  },
  async mounted() {
    this.getSurveyDetails();
  },
  methods: {
    selectRating(rating) {
      this.selectedRating = rating;
      this.updateSurveyDetails();
    },
    sendFeedback(message) {
      this.feedbackMessage = message;
      this.updateSurveyDetails();
    },
    async submitSurvey(feedbackData) {
      this.isUpdating = true;
      try {
        const data = {
          message: {
            submitted_values: {
              csat_survey_response: {
                rating: this.selectedRating,
                feedback_message: feedbackData.feedback,
                ease_of_interaction: feedbackData.easeOfInteraction,
                issue_resolution: feedbackData.issueResolution,
              },
            },
          },
        };
        await updateSurvey({ uuid: this.surveyId, data });
        this.surveyDetails = {
          rating: this.selectedRating,
          feedback_message: this.feedbackMessage,
          ease_of_interaction: feedbackData.easeOfInteraction,
          issue_resolution: feedbackData.issueResolution,
        };
        this.isSubmitted = true; // Set success state
        console.log('test')
        useAlert(this.$t('SURVEY.SUCCESS'));
      } catch (error) {
        const errorMessage = error?.response?.data?.error;
        this.errorMessage = errorMessage || this.$t('SURVEY.API.ERROR_MESSAGE');
        useAlert(this.errorMessage);
      } finally {
        this.isUpdating = false;
      }
    },
    async getSurveyDetails() {
      this.isLoading = true;
      try {
        const result = await getSurveyDetails({ uuid: this.surveyId });
        this.inboxName = result.data.inbox_name;
        this.inboxChannelName = result.data.inbox_channel_name;
        this.surveyDetails = result?.data?.csat_survey_response;
        this.selectedRating = this.surveyDetails?.rating;
        this.isSubmitted = result?.data?.csat_survey_response !== null;
        this.feedbackMessage = this.surveyDetails?.feedback_message || '';
        this.easeOfInteraction = this.surveyDetails?.ease_of_interaction || null;
        this.issueResolution = this.surveyDetails?.issue_resolution || null;
        this.setLocale(result.data.locale);
      } catch (error) {
        const errorMessage = error?.response?.data?.message;
        this.errorMessage = errorMessage || this.$t('SURVEY.API.ERROR_MESSAGE');
      } finally {
        this.isLoading = false;
      }
    },
    async updateSurveyDetails() {
      this.isUpdating = true;
      try {
        const data = {
          message: {
            submitted_values: {
              csat_survey_response: {
                rating: this.selectedRating,
                feedback_message: this.feedbackMessage,
                ease_of_interaction: this.easeOfInteraction,
                issue_resolution: this.issueResolution,
              },
            },
          },
        };
        await updateSurvey({
          uuid: this.surveyId,
          data,
        });
        this.surveyDetails = {
          rating: this.selectedRating,
          feedback_message: this.feedbackMessage,
          ease_of_interaction: this.easeOfInteraction,
          issue_resolution: this.issueResolution,
        };
      } catch (error) {
        const errorMessage = error?.response?.data?.error;
        this.errorMessage = errorMessage || this.$t('SURVEY.API.ERROR_MESSAGE');
        useAlert(this.errorMessage);
      } finally {
        this.isUpdating = false;
      }
    },
    setLocale(locale) {
      this.$root.$i18n.locale = locale || 'en';
    },
  },
};
</script>

<template>
  <div
    v-if="isLoading"
    class="flex items-center justify-center flex-1 h-full min-h-screen bg-black-25"
  >
    <Spinner size="" />
  </div>
  <div v-else class="flex justify-center w-full min-h-screen overflow-auto">
    <div
      v-if="!isSubmitted"
      class="flex flex-col w-full h-full bg-white rounded-lg lg:w-2/5 lg:h-auto"
    >
      <div class="w-full px-12 pt-12 pb-6 m-auto my-0">
        <img
          :src="logo"
          :alt="$t('SURVEY.LOGO.ALT')"
          class="mb-6 logo float-right d-block"
        />
        <div style="clear: both">
          <img :src="surveyTitleImage" alt="Survey tile" class="mb-6" />
        </div>
        <label class="mb-4 text-base font-medium text-black-800">
          {{ $t('SURVEY.RATING.LABEL') }}
        </label>
        <Rating
          :selected-rating="selectedRating"
          @select-rating="selectRating"
        />
        <div class="feedback-form">
          <Feedback
            :is-updating="isUpdating"
            :is-button-disabled="isButtonDisabled"
            :selected-rating="selectedRating"
            :channel-name="inboxChannelName"
            @send-feedback="submitSurvey"
          />
        </div>
      </div>
      <div class="mb-3">
        <Branding />
      </div>
    </div>
    <div
      v-else
      class="flex flex-col items-center justify-center w-full min-h-screen text-center bg-white rounded-lg lg:w-2/5 lg:h-auto"
    >
      <div class="text-green-500">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="w-16 h-16 mb-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 12l2 2l4-4m6 2a9 9 0 11-18 0a9 9 0 0118 0z"
          />
        </svg>
      </div>
      <h2 class="mb-4 text-lg font-semibold text-black-800">
        {{ $t('SURVEY.SUCCESS') || 'Thank you for your response!' }}
      </h2>
      <Branding />
    </div>
  </div>
</template>

<style scoped lang="scss">
@import 'widget/assets/scss/variables.scss';

.logo {
  max-height: $space-larger;
}
</style>
